
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Calendar, Search, Clock, User, MapPin, CheckCircle, XCircle, Trash2, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { useToast } from "@/hooks/use-toast";
import { AppointmentReceiptDialog } from "@/components/appointments/AppointmentReceiptDialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const MobileProfessionalAppointments = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedAppointmentId, setSelectedAppointmentId] = useState<string | null>(null);
  const [isReceiptDialogOpen, setIsReceiptDialogOpen] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: appointments, isLoading } = useQuery({
    queryKey: ["professional-appointments"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("professional_appointments")
        .select(`
          *,
          patient:profiles!patient_id(*),
          service:professional_services(*)
        `)
        .eq("professional_id", user.id)
        .order("requested_date", { ascending: true });

      if (error) throw error;
      return data;
    },
  });

  const updateAppointmentStatus = useMutation({
    mutationFn: async ({ appointmentId, status }: { appointmentId: string; status: string }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data: appointment, error: fetchError } = await supabase
        .from("professional_appointments")
        .select("status")
        .eq("id", appointmentId)
        .eq("professional_id", user.id)
        .single();
      
      if (fetchError) {
        console.error('Error fetching appointment for status check:', fetchError);
        throw new Error("Erreur lors de la récupération du rendez-vous.");
      }
      if (!appointment) {
        throw new Error("Rendez-vous introuvable.");
      }
      
      if (appointment.status !== 'pending') {
        throw new Error("Cette demande a déjà été mise à jour.");
      }

      const { data, error } = await supabase
        .from("professional_appointments")
        .update({ status })
        .eq("id", appointmentId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["professional-appointments"] });
      const statusText = variables.status === 'confirmed' ? 'confirmé' : 'annulé';
      toast({
        title: "Statut mis à jour",
        description: `Le rendez-vous a été ${statusText} avec succès`,
      });
    },
    onError: (error: Error) => {
      console.error('Error updating appointment:', error);
      toast({
        variant: "destructive",
        title: "Information",
        description: error.message || "Impossible de mettre à jour le statut du rendez-vous",
      });
    },
  });

  const deleteAppointment = useMutation({
    mutationFn: async (appointmentId: string) => {
      const { error } = await supabase
        .from("professional_appointments")
        .delete()
        .eq("id", appointmentId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["professional-appointments"] });
      toast({
        title: "Rendez-vous supprimé",
        description: "Le rendez-vous a été supprimé avec succès",
      });
    },
    onError: (error) => {
      console.error('Error deleting appointment:', error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de supprimer le rendez-vous",
      });
    },
  });

  const filteredAppointments = appointments?.filter(appointment => 
    appointment.patient?.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    appointment.service?.service_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "pending":
        return <Badge variant="outline" className="bg-amber-50 text-amber-600 border-amber-200">En attente</Badge>;
      case "confirmed":
        return <Badge variant="outline" className="bg-green-50 text-green-600 border-green-200">Confirmé</Badge>;
      case "canceled":
        return <Badge variant="outline" className="bg-red-50 text-red-600 border-red-200">Annulé</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const handleViewReceipt = (appointmentId: string) => {
    setSelectedAppointmentId(appointmentId);
    setIsReceiptDialogOpen(true);
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="sticky top-0 z-10 border-b bg-white px-4 py-3 flex items-center shadow-sm">
        <Button 
          variant="ghost" 
          size="icon" 
          className="mr-2" 
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="h-5 w-5" />
          <span className="sr-only">Retour</span>
        </Button>
        <h1 className="text-lg font-semibold text-gray-800">Mes rendez-vous</h1>
      </div>

      {/* Search */}
      <div className="p-4 space-y-4">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Rechercher un rendez-vous..."
              className="pl-9"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        {/* Appointments list */}
        <ScrollArea className="h-[calc(100vh-140px)]">
          <div className="space-y-3">
            {isLoading ? (
              Array(3).fill(0).map((_, i) => (
                <Card key={i} className="animate-pulse">
                  <CardContent className="p-0 h-32" />
                </Card>
              ))
            ) : filteredAppointments?.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                {searchQuery ? "Aucun rendez-vous trouvé" : "Vous n'avez pas encore de rendez-vous"}
              </div>
            ) : (
              filteredAppointments?.map((appointment) => (
                <Card key={appointment.id}>
                  <CardContent className="p-4">
                    <div className="flex flex-col space-y-3">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="font-medium">
                            {appointment.patient?.full_name || "Patient inconnu"}
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            {appointment.service?.service_name || "Service non spécifié"}
                          </p>
                        </div>
                        {getStatusBadge(appointment.status)}
                      </div>
                      
                      <div className="grid grid-cols-1 gap-2">
                        <div className="flex items-center gap-2 text-sm">
                          <Clock className="h-4 w-4 text-muted-foreground" />
                          <span>
                            {appointment.requested_date ? 
                              format(new Date(appointment.requested_date), "PPp", { locale: fr }) : 
                              "Date non spécifiée"}
                          </span>
                        </div>
                        
                        {appointment.service?.address && (
                          <div className="flex items-center gap-2 text-sm">
                            <MapPin className="h-4 w-4 text-muted-foreground" />
                            <span className="truncate">{appointment.service.address}</span>
                          </div>
                        )}
                      </div>
                      
                      {appointment.notes && (
                        <p className="text-sm bg-gray-50 p-2 rounded border">
                          {appointment.notes}
                        </p>
                      )}

                      {/* Action buttons */}
                      <div className="flex gap-2 pt-2">
                        {appointment.status === 'pending' && (
                          <>
                            <Button
                              variant="default"
                              size="sm"
                              onClick={() => updateAppointmentStatus.mutate({
                                appointmentId: appointment.id,
                                status: 'confirmed'
                              })}
                              disabled={updateAppointmentStatus.isPending}
                              className="flex items-center gap-1 flex-1"
                            >
                              <CheckCircle className="h-4 w-4" />
                              Accepter
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => updateAppointmentStatus.mutate({
                                appointmentId: appointment.id,
                                status: 'canceled'
                              })}
                              disabled={updateAppointmentStatus.isPending}
                              className="flex items-center gap-1 flex-1"
                            >
                              <XCircle className="h-4 w-4" />
                              Refuser
                            </Button>
                          </>
                        )}
                        
                        {appointment.status === 'confirmed' && (
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => handleViewReceipt(appointment.id)}
                            className="flex items-center gap-1"
                          >
                            <Eye className="h-4 w-4" />
                            Détails
                          </Button>
                        )}
                        
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="destructive"
                              size="sm"
                              disabled={deleteAppointment.isPending}
                              className="flex items-center gap-1"
                            >
                              <Trash2 className="h-4 w-4" />
                              Supprimer
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Supprimer le rendez-vous</AlertDialogTitle>
                              <AlertDialogDescription>
                                Êtes-vous sûr de vouloir supprimer ce rendez-vous ? Cette action est irréversible.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Annuler</AlertDialogCancel>
                              <AlertDialogAction 
                                onClick={() => deleteAppointment.mutate(appointment.id)}
                                disabled={deleteAppointment.isPending}
                              >
                                {deleteAppointment.isPending ? "Suppression..." : "Supprimer"}
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </ScrollArea>
      </div>

      {/* Dialog pour afficher les reçus */}
      <AppointmentReceiptDialog
        open={isReceiptDialogOpen}
        onOpenChange={setIsReceiptDialogOpen}
        appointmentId={selectedAppointmentId || ""}
      />
    </div>
  );
};

export default MobileProfessionalAppointments;
